import { mergeRefs } from "@regrello/core-utils";
import React, { useCallback } from "react";
import { type FieldPath, type FieldValues, useController } from "react-hook-form";
import { RegrelloFormFieldMultiSelect, type RegrelloFormFieldMultiSelectProps } from "../RegrelloFormFieldMultiSelect";
import type { FormFieldPropsToOmit } from "./types";
import { getErrorProp, type RegrelloControlledFormFieldProps, useInvokeOnChangeOnRequiredStateChange } from "./utils";

/**
 * A controlled multi-select field for use with react-hook-form.
 * The caller is responsible for loading the options and providing them into this component.
 */
function RegrelloControlledFormFieldMultiSelectInternal<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue,
>({
  controllerProps,
  onValueChange,
  ...multiSelectFieldProps
}: RegrelloControlledFormFieldProps<
  TFieldValues,
  TName,
  Omit<RegrelloFormFieldMultiSelectProps<TValue>, FormFieldPropsToOmit>
> & {
  /**
   * Capture the change event before the rerender happens. This is useful for setting the
   * "useForm" form values to the correct type before rendering a new RegrelloFormField of a
   * different type. This avoids passing in a incorrect data type to the component.
   */
  onValueChange?: (name: string, newValue: TValue[]) => void;
}) {
  const { field, fieldState } = useController(controllerProps);
  const { ref, ...fieldProps } = field;

  const internalOnBlur = fieldProps.onBlur;
  const propsOnBlur = multiSelectFieldProps.onBlur;

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      internalOnBlur();
      propsOnBlur?.(event);
    },
    [internalOnBlur, propsOnBlur],
  );

  useInvokeOnChangeOnRequiredStateChange(field, multiSelectFieldProps.isRequiredAsteriskShown ?? false);

  return (
    <RegrelloFormFieldMultiSelect
      {...fieldProps}
      {...multiSelectFieldProps}
      {...getErrorProp(fieldState)}
      inputRef={multiSelectFieldProps.inputRef != null ? mergeRefs(ref, multiSelectFieldProps.inputRef) : ref}
      onBlur={handleBlur}
      onChange={(newValue) => {
        onValueChange?.(field.name, newValue);
        fieldProps.onChange(newValue);
      }}
    />
  );
}

export const RegrelloControlledFormFieldMultiSelect = React.memo(
  RegrelloControlledFormFieldMultiSelectInternal,
) as typeof RegrelloControlledFormFieldMultiSelectInternal;
